import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  TextField, 
  Alert,
  CircularProgress,
  useTheme,
  Divider,
  
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import ComputerIcon from '@mui/icons-material/Computer';
import SchoolIcon from '@mui/icons-material/School';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SecurityIcon from '@mui/icons-material/Security';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(3),
  position: 'relative',
  '&:after': {
    content: '""',
    display: 'block',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(1),
  }
}));

const DonationCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  borderRadius: theme.spacing(1.5),
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
  },
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[5],
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(45, 45, 45, 0.9)' : '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '5px',
    height: '100%',
    backgroundColor: theme.palette.primary.main,
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: '50%',
  padding: theme.spacing(1.5),
  width: theme.spacing(6),
  height: theme.spacing(6),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const SecurityNotice = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
  borderRadius: theme.spacing(1),
  margin: theme.spacing(3, 0),
  '& .MuiSvgIcon-root': {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
  }
}));

const DonationAmountButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  fontWeight: 600,
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.primary.light : 'transparent',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.main : theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

const DonationForm = () => {
  const { t } = useTranslation();
  
  const [amount, setAmount] = useState('50');
  const [customAmount, setCustomAmount] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [stripe, setStripe] = useState(null);
  
  // Initialize Stripe
  useEffect(() => {
    // Load Stripe.js asynchronously
    const loadStripe = async () => {
      const stripeJs = await import('@stripe/stripe-js');
      const publishableKey =
        process.env.NODE_ENV === "development"
          ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_DEV
          : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_PROD;
      const stripePromise = stripeJs.loadStripe(publishableKey);
      setStripe(await stripePromise);
    };
    
    loadStripe();
  }, []);
  
  const donationOptions = [
    { value: '20', label: '$20' },
    { value: '50', label: '$50' },
    { value: '100', label: '$100' },
    { value: '250', label: '$250' },
    { value: 'custom', label: 'Custom Amount' }
  ];
  
  const handleAmountChange = (newAmount) => {
    setAmount(newAmount);
    setError('');
  };
  
  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      setCustomAmount(value);
      setError('');
    }
  };
  
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setFullName(newName);
    
    if (!newName) {
      setNameError('');
    } else if (newName.length < 2) {
      setNameError(t('donate.nameRequired'));
    } else {
      setNameError('');
    }
  };
  
  const handleNameBlur = () => {
    if (!fullName) {
      setNameError(t('donate.nameRequired'));
    } else if (fullName.length < 2) {
              setNameError(t('donate.nameRequired'));
    } else {
      setNameError('');
    }
  };
  
  const validateEmail = (email) => {
    // More comprehensive email regex pattern
    const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(String(email).toLowerCase());
  };
  
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Clear error on typing
    if (!newEmail) {
      setEmailError('');
      return;
    }
    
    // Validate as user types but don't show error immediately for better UX
    if (newEmail && newEmail.includes('@') && !validateEmail(newEmail)) {
      setEmailError(t('donate.validEmail'));
    } else {
      setEmailError('');
    }
  };
  
  // On blur validation is more aggressive
  const handleEmailBlur = () => {
    if (!email) {
      setEmailError(t('donate.emailRequired'));
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError(t('donate.validEmail'));
    } else {
      setEmailError('');
    }
  };
  
  const validateForm = () => {
    const finalAmount = amount === 'custom' ? customAmount : amount;
    let isValid = true;
    
    if (!finalAmount || parseFloat(finalAmount) <= 0) {
      setError(t('donate.validAmount'));
      isValid = false;
    }
    
    if (!email) {
      setEmailError(t('donate.emailRequired'));
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError(t('donate.validEmail'));
      isValid = false;
    }
    
    if (!fullName) {
      setNameError(t('donate.nameRequired'));
      isValid = false;
    } else if (fullName.length < 2) {
      setNameError(t('donate.nameRequired'));
      isValid = false;
    }
    
    return isValid;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      const finalAmount = amount === 'custom' ? customAmount : amount;
      
      // Use environment-specific API URL
      const apiUrl =
        process.env.NODE_ENV === "development"
          ? process.env.REACT_APP_API_URL_DEV
          : process.env.REACT_APP_API_URL_PROD;
      
      const response = await fetch(`${apiUrl}/donation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(parseFloat(finalAmount) * 100),
          currency: 'usd',
          name: fullName,
          email: email,
          successUrl: `${window.location.origin}${window.location.pathname.replace('/donate', '/thank-you')}`,
          cancelUrl: `${window.location.origin}${window.location.pathname}`,
          description: 'Donation to Digispark Foundation'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Donation error:', errorData);
        throw new Error('An error occurred while processing your donation. Please try again later.');
      }
      
      const { id } = await response.json();
      
      // Redirect to Stripe Checkout using Stripe.js
      if (stripe) {
        const { error } = await stripe.redirectToCheckout({
          sessionId: id
        });
        
        if (error) {
          console.error('Stripe redirect error:', error);
          throw new Error(error.message);
        }
      } else {
        // Fallback if Stripe.js hasn't loaded yet
        window.location.href = `https://checkout.stripe.com/pay/${id}`;
      }
      
    } catch (error) {
      console.error('Donation error:', error);
      setMessage(error.message || t('donate.donationError'));
      setMessageType('error');
      setLoading(false);
    }
  };
  
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {t('donate.makeDonation')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            {t('donate.supportDescription')}
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {t('donate.selectAmount')}
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {donationOptions.map((option) => 
              option.value !== 'custom' ? (
                <Grid item xs={6} sm={3} key={option.value}>
                  <DonationAmountButton
                    fullWidth
                    variant={amount === option.value ? "contained" : "outlined"}
                    selected={amount === option.value}
                    onClick={() => handleAmountChange(option.value)}
                  >
                    {option.label}
                  </DonationAmountButton>
                </Grid>
              ) : null
            )}
            <Grid item xs={6} sm={3}>
              <DonationAmountButton
                fullWidth
                variant={amount === 'custom' ? "contained" : "outlined"}
                selected={amount === 'custom'}
                onClick={() => handleAmountChange('custom')}
              >
                Custom
              </DonationAmountButton>
            </Grid>
          </Grid>
        </Grid>
        
        {amount === 'custom' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('donate.customAmount')}
              value={customAmount}
              onChange={handleCustomAmountChange}
              variant="outlined"
              InputProps={{
                startAdornment: '$',
              }}
              error={!!error}
              helperText={error}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                },
              }}
            />
          </Grid>
        )}
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('donate.fullName')}
            type="text"
            value={fullName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            variant="outlined"
            required
            error={!!nameError}
            helperText={nameError}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('donate.emailAddress')}
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            variant="outlined"
            required
            error={!!emailError}
            helperText={emailError}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <SecurityNotice>
            <SecurityIcon fontSize="small" />
            <Typography variant="body2">
              {t('donate.securePayment')}
            </Typography>
          </SecurityNotice>
        </Grid>
        
        {message && (
          <Grid item xs={12}>
            <Alert severity={messageType === 'success' ? 'success' : 'error'}>
              {message}
            </Alert>
          </Grid>
        )}
        
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={loading}
            startIcon={loading ? undefined : <VolunteerActivismIcon />}
            sx={{ 
              py: 1.5, 
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 1.5,
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <>{t('donate.donateNow')}</>
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

const Donate = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  return (
    <Box>
      {/* Donation Form Section - Now First */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <SectionTitle variant="h4">
                {t('donate.title')}
              </SectionTitle>
              <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary', fontWeight: 500 }}>
                {t('donate.subtitle')}
              </Typography>
              
              <Typography variant="body1" paragraph>
                {t('donate.description')}
              </Typography>
              
              <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  mr: 2, 
                  width: 42, 
                  height: 42, 
                  borderRadius: '50%', 
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ComputerIcon sx={{ color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('donate.100PercentPrograms')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('donate.100PercentDesc')}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  mr: 2, 
                  width: 42, 
                  height: 42, 
                  borderRadius: '50%', 
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CreditCardIcon sx={{ color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('donate.taxDeductible')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('donate.taxDeductibleDesc')}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  mr: 2, 
                  width: 42, 
                  height: 42, 
                  borderRadius: '50%', 
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AssessmentIcon sx={{ color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('donate.transparent')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('donate.transparentDesc')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <FormPaper elevation={5}>
                <DonationForm />
              </FormPaper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      <Divider />

      {/* Impact Section */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          <Typography variant="h4" align="center" sx={{ mb: 6, fontWeight: 700 }}>
            {t('donate.impactTitle')}
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <DonationCard elevation={2}>
                <IconWrapper>
                  <ComputerIcon />
                </IconWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('donate.technologyAccess')}
                </Typography>
                <Typography variant="body1">
                  {t('donate.technologyAccessDesc')}
                </Typography>
              </DonationCard>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <DonationCard elevation={2}>
                <IconWrapper>
                  <SchoolIcon />
                </IconWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('donate.digitalEducation')}
                </Typography>
                <Typography variant="body1">
                  {t('donate.digitalEducationDesc')}
                </Typography>
              </DonationCard>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <DonationCard elevation={2}>
                <IconWrapper>
                  <AgricultureIcon />
                </IconWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('donate.agtechInnovation')}
                </Typography>
                <Typography variant="body1">
                  {t('donate.agtechInnovationDesc')}
                </Typography>
              </DonationCard>
            </Grid>

            <Grid item xs={12} md={3}>
              <DonationCard elevation={2}>
                <IconWrapper>
                  <MedicalServicesIcon />
                </IconWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('donate.healthcareAccessibility')}
                </Typography>
                <Typography variant="body1">
                  {t('donate.healthcareAccessibilityDesc')}
                </Typography>
              </DonationCard>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Donate; 